import poolsConfig from 'config/constants/pools'
import masterChefABI from 'config/abi/masterchef.json'
import sousChefABI from 'config/abi/sousChef.json'
import bowABI from 'config/abi/bow.json'
import wcoreABI from 'config/abi/wcore.json'
import multicall from 'utils/multicall'
import { getAddress, getMasterChefAddress, getWcoreAddress } from 'utils/addressHelpers'
import { getStakerTokenPerSecond } from 'utils/apy'
import BigNumber from 'bignumber.js'

export const fetchPoolsBlockLimits = async () => {
  // Pools 0 and 8 are MasterChef pools: they have no bonusEndTime, so they never end.
  const poolsWithEnd = poolsConfig.filter((p) => ![0, 8].includes(p.sousId))

  const blockLimits = await Promise.all(
    poolsWithEnd.map(async (poolConfig) => {
      try {
        const [startTime] = await multicall(sousChefABI, [
          {
            address: getAddress(poolConfig.contractAddress),
            name: 'startTime',
          },
        ])
        const [endTime] = await multicall(sousChefABI, [
          {
            address: getAddress(poolConfig.contractAddress),
            name: 'bonusEndTime',
          },
        ])

        return {
          sousId: poolConfig.sousId,
          startTime: new BigNumber(startTime).toNumber(),
          endTime: new BigNumber(endTime).toNumber(),
        }
      } catch (error) {
        return {
          sousId: poolConfig.sousId,
          startTime: 0,
          endTime: 0,
        }
      }
    }),
  )

  return blockLimits
}

/** Fetches totalStaked for a single pool (one contract read). Use after stake/unstake for fast UI update. */
export const fetchPoolTotalStaking = async (sousId: number) => {
  const pool = poolsConfig.find((p) => p.sousId === sousId)
  if (!pool) return null
  const isCore = pool.stakingToken.symbol === 'CORE'
  const tokenAddress = isCore ? getWcoreAddress() : getAddress(pool.stakingToken.address)
  const contractAddress = getAddress(pool.contractAddress)
  const [balance] = await multicall(isCore ? wcoreABI : bowABI, [
    { address: tokenAddress, name: 'balanceOf', params: [contractAddress] },
  ])
  return { sousId, totalStaked: new BigNumber(balance).toJSON() }
}

export const fetchPoolsTotalStaking = async () => {
  const nonCorePools = poolsConfig.filter((p) => p.stakingToken.symbol !== 'CORE')
  const corePool = poolsConfig.filter((p) => p.stakingToken.symbol === 'CORE')

  const callsNonCorePools = nonCorePools.map((poolConfig) => {
    return {
      address: getAddress(poolConfig.stakingToken.address),
      name: 'balanceOf',
      params: [getAddress(poolConfig.contractAddress)],
    }
  })

  const callsCorePools = corePool.map((poolConfig) => {
    return {
      address: getWcoreAddress(),
      name: 'balanceOf',
      params: [getAddress(poolConfig.contractAddress)],
    }
  })

  const nonBnbPoolsTotalStaked = await multicall(bowABI, callsNonCorePools)
  const bnbPoolsTotalStaked = await multicall(wcoreABI, callsCorePools)
  const poolsWithAllocPoint = await Promise.all(
    nonCorePools.map(async (pool) => {
      if (![0, 8].includes(pool.sousId)) {
        return pool
      }
      const [info, totalAllocPoint, tokenPerSecond] = await multicall(masterChefABI, [
        {
          address: getMasterChefAddress(),
          name: 'poolInfo',
          params: [pool.sousId],
        },
        {
          address: getMasterChefAddress(),
          name: 'totalAllocPoint',
        },
        {
          address: getMasterChefAddress(),
          name: 'cakePerSecond',
        },
      ])

      const allocPoint = new BigNumber(info.allocPoint._hex)
      const poolWeight = allocPoint.div(new BigNumber(totalAllocPoint))

      return {
        ...pool,
        // tokenPerSecond: new BigNumber(tokenPerSecond).div(1e18).times(poolWeight).toJSON(),
        tokenPerSecond: getStakerTokenPerSecond(
          new BigNumber(tokenPerSecond).isZero() ? 200000000000000000 : tokenPerSecond,
        )
          .times(poolWeight)
          .toJSON(),
      }
    }),
  )

  return [
    ...poolsWithAllocPoint.map((p, index) => ({
      sousId: p.sousId,
      totalStaked: new BigNumber(nonBnbPoolsTotalStaked[index]).toJSON(),
      tokenPerSecond: p.tokenPerSecond,
    })),
    ...corePool.map((p, index) => ({
      sousId: p.sousId,
      totalStaked: new BigNumber(bnbPoolsTotalStaked[index]).toJSON(),
    })),
  ]
}
